import type { ChatMessage } from "../chat/types";

export type ToolGroup = { label: string; tools: string[]; note?: string };

export type Integration = {
  locked?: boolean;
  soon?: boolean;
  tagline: string;
  groups: ToolGroup[];
  msgs: ChatMessage[];
};

export const TAB_ORDER = [
  "Telegram",
  "HubSpot",
  "Notion",
  "Linear",
  "Apollo",
  "Google Calendar",
  "Gmail",
  "Luma",
  "Slack",
] as const;

export type IntegrationName = (typeof TAB_ORDER)[number];

export const INTEGRATIONS: Record<IntegrationName, Integration> = {
  Telegram: {
    locked: true,
    tagline:
      "Where deals actually live. Sidestack reads, organizes, and (with your OK) replies.",
    groups: [
      {
        label: "Knows your chats",
        tools: [
          "Find any conversation",
          "Pull a chat's history",
          "Search across every chat",
          "List your folders",
        ],
      },
      {
        label: "Keeps it tidy",
        tools: [
          "File chats into folders",
          "Archive what's done",
          "Pin what matters",
          "Mute the noise",
          "Mark threads read",
        ],
      },
      {
        label: "Replies for you",
        tools: ["Send messages on your behalf"],
        note: "off by default · you turn it on",
      },
    ],
    msgs: [
      { from: "user", text: "who hasn't replied in over a week?" },
      {
        from: "bot",
        text: "3 cold threads:\n\n• Marco @ Helius — 9 days · last said \"let me circle\"\n• Zach @ REM — 11 days · ghosted after deck\n• Dani @ Vector — 8 days · waiting on intro from you\n\nDrafted nudges for all 3. Send?",
      },
      { from: "bot", kind: "action", actions: ["Send all", "Review each"] },
    ],
  },
  HubSpot: {
    tagline: "Your CRM, kept clean — without you ever opening it.",
    groups: [
      {
        label: "Contacts",
        tools: [
          "Look up a person",
          "Add a new contact",
          "Update fields",
          "Search your book",
        ],
      },
      {
        label: "Companies",
        tools: [
          "Look up a company",
          "Add a new account",
          "Update fields",
          "Search by industry, size, anything",
        ],
      },
      {
        label: "Deals",
        tools: [
          "Pull any deal",
          "Create a deal from chat",
          "Move it through stages",
          "Search by status",
        ],
      },
      {
        label: "Wiring it together",
        tools: [
          "Link contacts to deals",
          "Link deals to companies",
          "Add call notes",
          "Find old notes",
        ],
      },
    ],
    msgs: [
      {
        from: "user",
        text: "log the helius deal — closed won, $40K ARR, marco's the contact",
      },
      {
        from: "bot",
        text: "Done. Deal HBS-8829 created · Closed Won · $40K ARR\n\n• Linked Marco Bianchi → Helius (CTO)\n• Pulled stage history from your Telegram thread\n• Attached the Notion call notes from Tuesday\n\nWant me to ping the team channel?",
      },
      { from: "bot", kind: "action", actions: ["Ping #wins", "Skip"] },
    ],
  },
  Notion: {
    tagline:
      "Drop call notes, briefs, and deal pages straight into your team's Notion.",
    groups: [
      {
        label: "Pages",
        tools: [
          "Find a page",
          "Read what's on it",
          "Create a new page",
          "Update or move pages",
        ],
      },
      {
        label: "Databases",
        tools: [
          "Open a database",
          "Query for matching rows",
          "Add a new database",
        ],
      },
      {
        label: "Inside a page",
        tools: ["Read the contents", "Add new sections, lists, or callouts"],
      },
      {
        label: "Collaboration",
        tools: ["Leave a comment", "See who's on the workspace"],
      },
    ],
    msgs: [
      { from: "user", text: "write up my call with sarah from modal" },
      {
        from: "bot",
        text: "Page created → /Sales / Active / Modal\n\n• Recap: 5 bullets covering scope, timeline, blockers\n• Action items: 3 (assigned to you · due dates set)\n• Linked: HubSpot deal · Apollo profile · prior 2 calls\n\nShared with @gtm-team.",
      },
    ],
  },
  Linear: {
    tagline: 'Turn "can you send me…" asks into tracked tickets, in chat.',
    groups: [
      {
        label: "Issues",
        tools: [
          "Open a new ticket",
          "Update status, owner, due date",
          "Search across the board",
          "Pull your own queue",
        ],
      },
      { label: "Comments", tools: ["Drop context into a thread"] },
    ],
    msgs: [
      { from: "user", text: "log REM deck request as a ticket" },
      {
        from: "bot",
        text: 'Created BD-142: "REM deck for Zach". Due Fri. Assigned to you.',
      },
    ],
  },
  Apollo: {
    tagline:
      "Find anyone. Get their email, title, company. Add them to your book.",
    groups: [
      {
        label: "People",
        tools: [
          "Find a person by name + company",
          "Pull their email, title, LinkedIn",
        ],
      },
      {
        label: "Companies",
        tools: ["Find a company", "Pull headcount, funding, industry"],
      },
      {
        label: "Your book",
        tools: [
          "Search your saved contacts",
          "Add a new one",
          "Update what you know",
        ],
      },
    ],
    msgs: [
      { from: "user", text: "find sarah from the ai dinner last thursday" },
      {
        from: "bot",
        text: "Sarah Park · Director of BD at Modal\nspark@modal.com · linkedin.com/in/sarahpark\n\nThey just hired 4 GTM in the last 30d — likely scaling sales motion. Draft a follow-up that opens with that?",
      },
      { from: "bot", kind: "action", actions: ["Draft it", "Just save her"] },
    ],
  },
  "Google Calendar": {
    tagline:
      "Read your week. Schedule on your behalf. Brief you before every call.",
    groups: [
      {
        label: "Your calendar",
        tools: [
          "See what's on the day",
          "Book a new meeting",
          "Reschedule or cancel",
        ],
      },
      {
        label: "Find time",
        tools: [
          "Check when you're free",
          "Suggest slots that work for both sides",
        ],
      },
    ],
    msgs: [
      { from: "user", text: "what's my 4pm and what should i know?" },
      {
        from: "bot",
        text: "⏱️ 4pm · Pascal Lambert · Focal VC\n\n• Last touch: 12 days ago, asked for Q3 numbers\n• Open thread: still owe him runway\n• Warm signal: he forwarded your deck to 2 partners\n\nLead with: \"Q3 landed at $1.4M MRR, 18 mo runway.\" That's the unlock.",
      },
    ],
  },
  Gmail: {
    tagline:
      "Read what's in your inbox. Draft replies in your voice. Send when you say so.",
    groups: [
      {
        label: "Read",
        tools: [
          "Search your inbox",
          "Pull any email's contents",
          "See your labels",
        ],
      },
      {
        label: "Write",
        tools: [
          "Draft a new email",
          "Reply to a thread",
          "Send (only after you confirm)",
        ],
      },
    ],
    msgs: [
      { from: "user", text: "send sarah the deck and bcc me" },
      {
        from: "bot",
        text: "Drafted in your voice (matched against your last 3 sends to her):\n\nSubject: As promised — Sidestack overview\n\n\"Hey Sarah, ping from yesterday — here's the deck. Q3 numbers are on slide 8. Lmk if you want to grab 15 min next week to walk through.\"\n\nBCC'd you. Send?",
      },
      { from: "bot", kind: "action", actions: ["Send", "Edit"] },
    ],
  },
  Luma: {
    tagline: "Map every event you're going to against your live deal book.",
    groups: [
      {
        label: "Events",
        tools: ["List your events", "Pull event details", "Create a new one"],
      },
      {
        label: "Guests",
        tools: ["See who's coming", "Pull a guest's info"],
      },
    ],
    msgs: [
      {
        from: "user",
        text: "who from my list is at the Solana dinner tomorrow?",
      },
      {
        from: "bot",
        text: "4 of your active deals RSVP'd:\nMarco @ Helius, Dani @ Vector, Sarah @ Modal, Zach @ REM.\nWant a brief on each before doors open?",
      },
    ],
  },
  Slack: {
    soon: true,
    tagline: "Bring chat-driven BD into your team channels.",
    groups: [
      {
        label: "Read",
        tools: ["List channels", "Pull recent messages", "Search across channels"],
      },
      {
        label: "Write",
        tools: ["Post to a channel", "Reply in a thread"],
      },
    ],
    msgs: [
      {
        from: "bot",
        text: "Slack support is in active design. Talk to us if you want it shaped to your team's flow.",
      },
    ],
  },
};
